import { useContext, useEffect, useRef } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import fetcher from '../../../fetcher'
import { UserContext } from '../../../contexts/UserContext'

// A CRM page's Driver.js tour, saved per user under `tutorials[key]`. The page
// owns its steps and decides when it is ready: it calls `autoStart` once its
// data has loaded, and `replay` from its Show Tutorial button.
export const usePageTour = ({ key, version, steps }) => {
    // Optional: pages render bare in tests, which then never auto-tour.
    const { profileState, setProfileState } = useContext(UserContext) || {}
    const tourRef = useRef(null)
    const autoStarted = useRef(false)

    // Sends only this page's tour (the server merges it into `tutorials`), and
    // merges into the profile as it is now: a Driver.js callback holds the
    // profileState of the render that started it.
    const saveTour = (value) => {
        setProfileState((p) => ({ ...p, tutorials: { ...p?.tutorials, [key]: value } }))
        fetcher.updateProfile({ tutorials: { [key]: value } }).catch(() => {})
    }

    // `auto` is the first-visit run: it resumes at the saved step (or the next
    // one still on screen) and saves each step it reaches. A replay starts over
    // and only records Done. X, Escape and leaving save nothing — the step is
    // already saved, so they pause.
    const startTour = (auto) => {
        const saved = profileState?.tutorials?.[key]
        let savedId = auto && saved?.version === version ? saved.step : undefined
        const savedAt = steps.findIndex((s) => s.data.id === savedId)
        const shown = steps.filter((s) => document.querySelector(s.element))
        const complete = () => saveTour({ version, completed: true })

        tourRef.current = driver({
            steps: shown,
            showProgress: true,
            disableActiveInteraction: true,
            popoverClass: 'crm-tour',
            onHighlighted: (el, step) => {
                if (!auto || step.data.id === savedId) return
                savedId = step.data.id
                saveTour({ version, step: savedId })
            },
            onDoneClick: (el, step, { driver: tour }) => {
                complete()
                tour.destroy()
            },
            onPopoverRender: (popover, { driver: tour }) => {
                if (!auto) return
                const skip = document.createElement('button')
                skip.className = 'crm-tour-skip'
                skip.textContent = 'Skip tutorial'
                skip.onclick = () => {
                    complete()
                    tour.destroy()
                }
                popover.footerButtons.prepend(skip)
            },
        })
        tourRef.current.drive(shown.findIndex((s) => steps.indexOf(s) >= savedAt))
    }

    // Once per mount, until it is Done or skipped.
    const autoStart = () => {
        if (autoStarted.current || !profileState) return
        const saved = profileState.tutorials?.[key]
        if (saved?.version === version && saved.completed) return
        autoStarted.current = true
        startTour(true)
    }

    // A tour left running keeps `pointer-events: none` on the whole app.
    useEffect(() => () => tourRef.current?.destroy(), [])

    return { autoStart, replay: () => startTour(false) }
}
